'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface OriginalFile { name: string; url: string; }
interface AssessmentData { title: string; instructions: string; isOpen: boolean; originalFiles: OriginalFile[]; }

const SERVICE_TYPES = [
  'HDR Processing',
  'Image Enhancement',
  'Item Removal',
  'Virtual Staging',
  'Virtual Renovation',
  'Day to Dusk',
  'Floor Plan',
];

export default function AssessmentClient({ token }: { token: string }) {
  const [assessment, setAssessment] = useState<AssessmentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');
  const [specs, setSpecs] = useState<Record<string, { checked: boolean; price: string }>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetch(`/api/assessments/${token}`)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((d) => setAssessment(d.assessment))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const MAX_FILES = 2;
  const isJpg = (f: File) => /\.jpe?g$/i.test(f.name) || f.type === 'image/jpeg';

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    setError('');
    const incoming = Array.from(list);

    const nonJpg = incoming.filter((f) => !isJpg(f));
    if (nonJpg.length > 0) {
      setError('Only JPG files are allowed.');
    }
    const jpgs = incoming.filter(isJpg);

    setFiles((prev) => {
      const combined = [...prev, ...jpgs];
      if (combined.length > MAX_FILES) {
        setError(`You can upload a maximum of ${MAX_FILES} JPG files.`);
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });
  };

  const removeFile = (i: number) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const toggleSpec = (service: string) => setSpecs((prev) => ({
    ...prev,
    [service]: { checked: !prev[service]?.checked, price: prev[service]?.price || '' },
  }));
  const setSpecPrice = (service: string, price: string) => setSpecs((prev) => ({
    ...prev,
    [service]: { checked: prev[service]?.checked ?? true, price },
  }));

  const submit = async () => {
    setError('');
    if (files.length === 0) { setError('Please add at least one edited JPG file.'); return; }
    if (files.length > MAX_FILES) { setError(`You can upload a maximum of ${MAX_FILES} JPG files.`); return; }
    if (!files.every(isJpg)) { setError('Only JPG files are allowed.'); return; }
    setSubmitting(true);
    try {
      const uploaded: { name: string; objectKey: string }[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress(`Uploading ${i + 1} of ${files.length}...`);
        const res = await fetch('/api/assessments/upload-url', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, kind: 'submission', fileName: file.name, fileSize: file.size }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || 'Upload failed');
        const put = await fetch(json.signedUploadUrl, { method: 'PUT', body: file });
        if (!put.ok) throw new Error(`Failed to upload ${file.name}`);
        uploaded.push({ name: file.name, objectKey: json.objectKey });
      }
      setProgress('Finishing...');
      const specializations = Object.entries(specs)
        .filter(([, v]) => v.checked)
        .map(([service, v]) => ({ service, price: v.price.trim() }));
      const submitRes = await fetch(`/api/assessments/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidateName: name, candidateEmail: email, country, phone, specializations, files: uploaded }),
      });
      const submitJson = await submitRes.json();
      if (!submitRes.ok) throw new Error(submitJson.error || 'Submission failed');
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
      setProgress('');
    }
  };

  if (loading) {
    return <Shell><div className="flex justify-center py-24"><div className="h-10 w-10 animate-spin rounded-full border-b-2 border-purple-500" /></div></Shell>;
  }
  if (notFound || !assessment) {
    return <Shell><Card><div className="text-center py-10"><Icon name="ExclamationTriangleIcon" size={40} className="text-amber-400 mx-auto mb-3" /><h1 className="text-xl font-bold text-white">Assessment not found</h1><p className="text-gray-400 mt-2 text-sm">This link may be invalid or expired. Please check with your contact at Shashio.</p></div></Card></Shell>;
  }
  if (submitted) {
    return <Shell><Card><div className="text-center py-10"><div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-400/30"><Icon name="CheckCircleIcon" size={30} className="text-emerald-400" /></div><h1 className="text-2xl font-bold text-white">Submission received</h1><p className="text-gray-400 mt-2">Thanks{name ? `, ${name}` : ''}! Our team will review your edits and get back to you.</p></div></Card></Shell>;
  }

  return (
    <Shell>
      <div className="space-y-5">
        {/* Header */}
        <Card>
          <div className="flex items-start gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 shadow-lg shadow-purple-950/50"><Icon name="AcademicCapIcon" size={24} className="text-white" /></div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-purple-300">Editor skill assessment</p>
              <h1 className="mt-1 text-2xl font-bold text-white">{assessment.title}</h1>
              <p className="mt-1 text-sm text-gray-400">Download the files, edit them, then upload your finished work below.</p>
            </div>
          </div>
          {!assessment.isOpen && <div className="mt-4 rounded-lg border border-amber-400/30 bg-amber-500/10 px-4 py-2.5 text-sm text-amber-200">This assessment is currently closed for new submissions.</div>}
        </Card>

        {/* Instructions */}
        {assessment.instructions && (
          <Card>
            <h2 className="flex items-center gap-2 text-sm font-bold text-white"><Icon name="DocumentTextIcon" size={17} className="text-purple-300" /> Instructions</h2>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-300">{assessment.instructions}</p>
          </Card>
        )}

        {/* Download originals */}
        <Card>
          <h2 className="flex items-center gap-2 text-sm font-bold text-white"><Icon name="FolderArrowDownIcon" size={17} className="text-purple-300" /> Original files ({assessment.originalFiles.length})</h2>
          {assessment.originalFiles.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No files attached yet.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {assessment.originalFiles.map((f) => (
                <a key={f.url} href={f.url} download className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-black/20 px-3 py-2.5 transition-colors hover:border-purple-400/25 hover:bg-white/[0.04]">
                  <Icon name="DocumentIcon" size={17} className="text-gray-500 group-hover:text-purple-300" />
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-200">{f.name}</span>
                  <Icon name="ArrowDownTrayIcon" size={16} className="text-gray-500 group-hover:text-purple-300" />
                </a>
              ))}
            </div>
          )}
        </Card>

        {/* Upload submission */}
        <Card>
          <h2 className="flex items-center gap-2 text-sm font-bold text-white"><Icon name="CloudArrowUpIcon" size={17} className="text-purple-300" /> Your submission</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="rounded-xl border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none" />
            <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" type="email" className="rounded-xl border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none" />
            <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Country" className="rounded-xl border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" className="rounded-xl border border-white/[0.12] bg-white/[0.03] px-4 py-2.5 text-sm text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none" />
          </div>

          {/* Specializations + rates */}
          <div className="mt-4">
            <p className="text-sm font-semibold text-white">What are you specialized in?</p>
            <p className="mb-2 text-xs text-gray-500">Select the edit types you can do and your rate per image (USD).</p>
            <div className="space-y-2">
              {SERVICE_TYPES.map((service) => {
                const s = specs[service];
                const checked = !!s?.checked;
                return (
                  <div key={service} className={`rounded-xl border px-3 py-2.5 transition-colors ${checked ? 'border-purple-400/30 bg-purple-500/10' : 'border-white/[0.08] bg-black/20'}`}>
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex cursor-pointer items-center gap-2.5 text-sm text-gray-200">
                        <input type="checkbox" checked={checked} onChange={() => toggleSpec(service)} className="h-4 w-4 accent-purple-600" />
                        {service}
                      </label>
                      {checked && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-gray-400">$</span>
                          <input value={s?.price || ''} onChange={(e) => setSpecPrice(service, e.target.value)} placeholder="0.00" inputMode="decimal" className="w-24 rounded-lg border border-white/[0.12] bg-white/[0.03] px-2.5 py-1.5 text-sm text-white placeholder-gray-600 focus:border-purple-500 focus:outline-none" />
                          <span className="text-xs text-gray-500">/ image</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            onClick={() => { if (files.length < MAX_FILES) inputRef.current?.click(); }}
            className={`mt-3 rounded-xl border border-dashed p-6 text-center transition-colors ${files.length >= MAX_FILES ? 'cursor-not-allowed border-white/10 bg-white/[0.02] opacity-60' : 'cursor-pointer border-purple-400/30 bg-gradient-to-br from-purple-600/[0.08] to-transparent hover:border-purple-300'}`}
          >
            <Icon name="ArrowUpTrayIcon" size={22} className="mx-auto text-purple-300" />
            <p className="mt-2 text-sm font-semibold text-white">{files.length >= MAX_FILES ? 'Maximum of 2 files added' : 'Click to add your edited files'}</p>
            <p className="text-xs text-gray-500">JPG only · maximum 2 files · up to 100MB each</p>
            <input ref={inputRef} type="file" accept=".jpg,.jpeg,image/jpeg" multiple onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
          </div>

          {files.length > 0 && (
            <div className="mt-3 space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2 text-sm">
                  <Icon name="PhotoIcon" size={15} className="text-purple-300" />
                  <span className="min-w-0 flex-1 truncate text-gray-200">{f.name}</span>
                  <button onClick={() => removeFile(i)} className="text-gray-500 hover:text-red-400"><Icon name="XMarkIcon" size={15} /></button>
                </div>
              ))}
            </div>
          )}

          {error && <p className="mt-3 rounded-lg border border-red-400/25 bg-red-500/10 px-3 py-2 text-sm text-red-200">{error}</p>}

          <button onClick={submit} disabled={submitting || !assessment.isOpen} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-500 px-5 py-3 text-sm font-bold text-white transition-all hover:from-purple-500 hover:to-purple-400 disabled:opacity-50">
            {submitting ? <><Icon name="ArrowPathIcon" size={16} className="animate-spin text-white" /> {progress || 'Submitting...'}</> : <>Submit assessment</>}
          </button>
        </Card>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="relative min-h-screen overflow-x-hidden bg-[#0B0D17] py-8 px-4">
      <div className="pointer-events-none absolute inset-0"><div className="absolute -top-40 left-1/3 h-[500px] w-[500px] rounded-full bg-purple-600/10 blur-[130px]" /></div>
      <div className="relative mx-auto max-w-2xl">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-purple-700"><Icon name="SparklesIcon" size={16} className="text-white" /></div>
          <span className="text-lg font-bold text-white">shashio</span>
        </div>
        {children}
      </div>
    </main>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return <section className="rounded-2xl border border-white/[0.08] bg-[#13151F]/90 p-5 shadow-xl shadow-black/20">{children}</section>;
}
